import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cx } from './cx';

/**
 * Hierarquia de ação explícita.
 *
 * `variant` é obrigatório de propósito. Com valor padrão, todo botão do produto
 * acabaria `primary` por omissão — foi exatamente o que aconteceu no front
 * anterior, onde telas tinham três botões roxos e nenhuma ação principal
 * identificável.
 *
 *   primary    uma por tela (no máximo). A ação que conclui a tarefa.
 *   secondary  alternativa legítima: cancelar, voltar, ação paralela.
 *   tertiary   navegação e ações de baixo peso dentro de listas e cabeçalhos.
 *   danger     destrutiva EM CONFIRMAÇÃO: o botão que efetiva a exclusão
 *              dentro do diálogo. Nunca é `primary`, mesmo sendo a única.
 *   danger-quiet  destrutiva em linha de tabela. Existe porque a versão
 *              preenchida repetida numa lista de 30 linhas produz 30 blocos
 *              vermelhos — a ação mais perigosa vira a mais chamativa da tela,
 *              que é o oposto da hierarquia que se quer.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'danger-quiet';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant: ButtonVariant;
  size?: 'md' | 'sm';
  /** Mantém a largura e desabilita; troca o conteúdo por indicador, não por texto. */
  loading?: boolean;
  /** Rótulo anunciado enquanto carrega. Sem isso o leitor de tela só ouve silêncio. */
  loadingLabel?: string;
  icon?: ReactNode;
  children: ReactNode;
}

export function Button({
  variant,
  size = 'md',
  loading = false,
  loadingLabel = 'Processando',
  icon,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={cx('btn', `btn--${size}`, `btn--${variant}`)}
      data-loading={loading || undefined}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="btn__spinner" aria-hidden="true" /> : icon}
      {children}
      {loading && <span className="sr-only">{loadingLabel}</span>}
    </button>
  );
}
